import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Check, ChevronsUpDown, BookOpen, Clock } from "lucide-react";
import { cn } from "@/lib/utils";

interface TopicSwitcherProps {
  currentTopicId?: string;
  onTopicChange: (topicId: string | undefined) => void;
  contentType?: 'mcq' | 'notes';
}

interface TopicWithProgress {
  id: string;
  name: string;
  progress: number;
  lastAccessed?: number;
}

export function TopicSwitcher({ currentTopicId, onTopicChange, contentType = 'mcq' }: TopicSwitcherProps) {
  const [open, setOpen] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUserId(session?.user?.id ?? null);
    });
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUserId(session?.user?.id ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);

  const { data: topics = [] } = useQuery({
    queryKey: ["topics"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("topics")
        .select("id, name")
        .order("name", { ascending: true });
      if (error) throw error;
      return data as { id: string; name: string }[];
    },
  });

  // Get progress for each topic (simplified - just show recent access)
  const topicProgress: Record<string, number> = {};

  // Get recently viewed topics from localStorage
  const getRecentTopics = (): string[] => {
    try {
      const recent = localStorage.getItem('recent_topics');
      return recent ? JSON.parse(recent) : [];
    } catch {
      return [];
    }
  };

  const [recentTopicIds] = useState<string[]>(getRecentTopics().slice(0, 5));

  const updateRecentTopics = (topicId: string) => {
    try {
      const recent = getRecentTopics();
      const updated = [topicId, ...recent.filter(id => id !== topicId)].slice(0, 10);
      localStorage.setItem('recent_topics', JSON.stringify(updated));
    } catch (e) {
      console.error('Failed to update recent topics', e);
    }
  };

  const topicsWithProgress: TopicWithProgress[] = topics.map(topic => ({
    ...topic,
    progress: topicProgress[topic.id] || 0,
    lastAccessed: recentTopicIds.indexOf(topic.id),
  }));

  const recentTopics = topicsWithProgress
    .filter(t => recentTopicIds.includes(t.id))
    .sort((a, b) => (a.lastAccessed || 999) - (b.lastAccessed || 999));

  const otherTopics = topicsWithProgress.filter(t => !recentTopicIds.includes(t.id));

  const currentTopic = topics.find(t => t.id === currentTopicId);

  const handleSelect = (topicId: string | undefined) => {
    if (topicId) {
      updateRecentTopics(topicId);
    }
    onTopicChange(topicId);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-[250px] justify-between hover-lift"
        >
          <div className="flex items-center gap-2 truncate">
            <BookOpen className="h-4 w-4 flex-shrink-0" />
            <span className="truncate">
              {currentTopic ? currentTopic.name : "All Topics"}
            </span>
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0 z-50 bg-background" align="start">
        <Command>
          <CommandInput placeholder="Search topics..." className="h-9" />
          <CommandList>
            <CommandEmpty>No topics found.</CommandEmpty>
            
            {/* All Topics Option */}
            <CommandGroup heading="View All">
              <CommandItem
                value="all-topics"
                onSelect={() => handleSelect(undefined)}
              >
                <Check
                  className={cn(
                    "mr-2 h-4 w-4",
                    !currentTopicId ? "opacity-100" : "opacity-0"
                  )}
                />
                <span>All Topics</span>
              </CommandItem>
            </CommandGroup>

            {/* Recent Topics */}
            {recentTopics.length > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup heading="Recently Viewed">
                  {recentTopics.map((topic) => (
                    <CommandItem
                      key={topic.id}
                      value={topic.id}
                      onSelect={() => handleSelect(topic.id)}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          currentTopicId === topic.id ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="truncate">{topic.name}</span>
                          {topic.progress > 0 && (
                            <Badge variant="secondary" className="text-xs">
                              {Math.round(topic.progress)}%
                            </Badge>
                          )}
                        </div>
                        {topic.progress > 0 && (
                          <Progress value={topic.progress} className="h-1 mt-1" />
                        )}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}

            {/* Other Topics */}
            {otherTopics.length > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup heading="All Topics">
                  {otherTopics.map((topic) => (
                    <CommandItem
                      key={topic.id}
                      value={topic.id}
                      onSelect={() => handleSelect(topic.id)}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          currentTopicId === topic.id ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="truncate">{topic.name}</span>
                          {topic.progress > 0 && (
                            <Badge variant="secondary" className="text-xs">
                              {Math.round(topic.progress)}%
                            </Badge>
                          )}
                        </div>
                        {topic.progress > 0 && (
                          <Progress value={topic.progress} className="h-1 mt-1" />
                        )}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
